// packages/shared/src/catalog/primitives.ts
//
// Catalog primitive Zod schemas + TypeScript types shared across the entire
// catalog module (Wave 2-7 tasks import from here — do NOT redeclare).
//
// Decisions implemented:
//   - i18n — EN required, VI/FR optional (align with SupportedLocale in i18n/init.ts)
//   - USD lock M1 — CurrencyCode is strict z.enum(['USD']) for Milestone 1
//
// Per FOUNDATION.md F-01 (platform choice): monetary values flow as integer
// cents (no decimals on the wire). Aligns with currency.ts formatCents/parseCents
// which already document "cents MUST be an integer".
//
// FIX-13 boundary: NO Fastify / Mongoose imports. Node-pure. Safe to bundle in Vite.
import { z } from 'zod';
// ---------------------------------------------------------------------------
// I18nString — (en required, vi/fr optional)
//
// Aligns with SupportedLocale = 'vi' | 'en' | 'fr' from i18n/init.ts.
// The base schema fixes `en` as required. Use the helper `i18nStringSchema(opts)`
// when a caller needs to enforce additional required locales (e.g. vendor data
// that MUST have both en + vi).
// ---------------------------------------------------------------------------
/**
 * Base I18n string object. `en` is always required.
 * `vi` and `fr` are optional — content teams fill them in over time.
 *
 * @example
 *   I18nStringSchema.parse({ en: 'Hello' })             // PASS
 *   I18nStringSchema.parse({ en: 'Hi', vi: 'Xin chào' }) // PASS
 *   I18nStringSchema.parse({ vi: 'Xin' })               // FAIL — en required
 */
export const I18nStringSchema = z.object({
    en: z.string().min(1, 'en locale string must not be empty'),
    vi: z.string().optional(),
    fr: z.string().optional(),
});
/**
 * Factory that returns an I18nString schema with a caller-specified
 * required-locale matrix. Default (no args) is identical to `I18nStringSchema`
 * (only `en` required).
 *
 * Downstream modules (vendor data ingestion, export jobs) use this to
 * enforce multi-locale completeness without duplicating schema logic.
 *
 * @example
 *   const schema = i18nStringSchema({ requiredLocales: ['en', 'vi'] });
 *   schema.parse({ en: 'Hello', vi: 'Xin chào' }) // PASS
 *   schema.parse({ en: 'Hello' })                 // FAIL — vi required
 */
export function i18nStringSchema(opts) {
    const required = new Set(opts?.requiredLocales ?? ['en']);
    // en is ALWAYS required — invariant
    required.add('en');
    const makeField = (locale) => required.has(locale)
        ? z.string().min(1, `${locale} locale string must not be empty`)
        : z.string().optional();
    return z.object({
        en: makeField('en'),
        vi: makeField('vi'),
        fr: makeField('fr'),
    });
}
// ---------------------------------------------------------------------------
// MoneyCents — Int64 cents canonical (no decimals)
//
// Aligns with currency.ts: "cents MUST be an integer" invariant.
//  / F-01: all monetary values cross the wire as integer cents.
// ---------------------------------------------------------------------------
/**
 * Non-negative integer representing a monetary amount in the currency's
 * smallest unit (cents for USD/EUR, won for KRW, etc.).
 *
 * @example
 *   MoneyCentsSchema.parse(1099) // PASS — $10.99
 *   MoneyCentsSchema.parse(0)    // PASS — $0.00
 *   MoneyCentsSchema.parse(1.5)  // FAIL — int required
 *   MoneyCentsSchema.parse(-1)   // FAIL — must be >= 0
 */
export const MoneyCentsSchema = z
    .number()
    .int('MoneyCents must be an integer (no decimal cents)')
    .min(0, 'MoneyCents must be non-negative');
// ---------------------------------------------------------------------------
// CatalogCurrencyCode — M1 USD lock / F-01
//
// Named `CatalogCurrencyCode` (not plain `CurrencyCode`) to avoid a
// barrel-level name collision with `CurrencyCode = string` exported by
// `currency.ts`. Same pattern as `LedgerCurrencyCode` in `ledger/types.ts`.
//
// Strict enum in Milestone 1. Will expand (e.g. add 'EUR') in a future
// milestone via a CONTEXT decision — never widen this unilaterally.
// ---------------------------------------------------------------------------
/**
 * Catalog-specific currency code schema. Strict USD-only in M1.
 * Callers attempting to pass 'VND', 'EUR', etc. receive a Zod error at
 * runtime, surfacing currency mismatches early.
 *
 * Named `CatalogCurrencyCodeSchema` to avoid collision with the broader
 * `CurrencyCode` type exported by `currency.ts` (which is `string`).
 *
 * @example
 *   CatalogCurrencyCodeSchema.parse('USD') // PASS
 *   CatalogCurrencyCodeSchema.parse('VND') // FAIL — USD strict M1
 *   CatalogCurrencyCodeSchema.parse('EUR') // FAIL — USD strict M1
 */
export const CatalogCurrencyCodeSchema = z.enum(['USD']);
// ---------------------------------------------------------------------------
// AreaUnit — physical dimension units for print blueprint dimensions
// ---------------------------------------------------------------------------
/**
 * Units for physical measurements (print bleed, canvas size, etc.).
 *
 * @example
 *   AreaUnitSchema.parse('in')  // PASS — inches
 *   AreaUnitSchema.parse('cm')  // PASS — centimetres
 *   AreaUnitSchema.parse('mm')  // PASS — millimetres
 *   AreaUnitSchema.parse('px')  // FAIL
 */
export const AreaUnitSchema = z.enum(['in', 'cm', 'mm']);
// ---------------------------------------------------------------------------
// BasisPoints — fractional percentage represented as integer (0–10000)
//
// 10000 basis points = 100%. Used for discount rates, platform fees,
// commission splits. Integer representation avoids floating-point drift
// in percentage math.
//
// @example
//   BasisPointsSchema.parse(500)   // PASS — 5%
//   BasisPointsSchema.parse(10000) // PASS — 100%
//   BasisPointsSchema.parse(10001) // FAIL — exceeds max
//   BasisPointsSchema.parse(-1)    // FAIL — below min
// ---------------------------------------------------------------------------
/**
 * Integer basis points value in [0, 10000] (0% – 100%).
 * Fractional values such as 0.5 basis points are not representable —
 * use integer rounding at the point of calculation.
 */
export const BasisPointsSchema = z
    .number()
    .int('BasisPoints must be an integer')
    .min(0, 'BasisPoints must be >= 0')
    .max(10000, 'BasisPoints must be <= 10000 (100%)');
//# sourceMappingURL=primitives.js.map